import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { QC_ISSUE_CATEGORY_LABELS } from '../../../interfaces/qcIssue.interface';
import { QC_CATEGORY_RGB, formatQcDate, ACCENT_BLUE, SUCCESS_GREEN, ERROR_RED } from './qcTheme';
import { countQcCategories, QcSummaryRow } from './workspaceDerivations';
import {
  CategoryChip,
  CloseButton,
  OutlinedButton,
  PrimaryButton,
  QuestionCode,
} from './workspaceModalParts';

const VISIBLE_ROWS = 5;

const Header = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '24px 32px 18px',
  borderBottom: '1px solid rgba(0,0,0,0.12)',
});

const Title = styled('h2')({
  fontSize: '1.45rem',
  fontWeight: 400,
  color: 'rgba(0,0,0,0.87)',
  margin: 0,
});

const Lede = styled('p')({
  fontSize: '0.9rem',
  lineHeight: 1.55,
  color: 'rgba(0,0,0,0.65)',
  margin: '6px 0 0',
  maxWidth: '840px',
});

const MetaStrip = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  flexWrap: 'wrap',
  padding: '14px 32px',
  background: '#f3f5fb',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
  flex: 'none',
});

const MetaItem = styled('span')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const MetaLabel = styled('span')({
  fontSize: '0.68rem',
  letterSpacing: '0.5px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.45)',
  textTransform: 'uppercase',
});

const MetaValue = styled('span', {
  shouldForwardProp: prop => prop !== 'strong',
})<{ strong?: boolean }>(({ strong }) => ({
  fontSize: '0.875rem',
  fontWeight: strong ? 600 : 400,
  color: strong ? '#424242' : 'rgba(0,0,0,0.75)',
}));

const MetaDivider = styled('span')({
  width: '1px',
  height: '16px',
  background: 'rgba(0,0,0,0.14)',
  flex: 'none',
});

const Body = styled('div')({
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  padding: '22px 32px 8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
});

const IssuesPanel = styled('div')({
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: '6px',
  overflow: 'hidden',
});

const IssuesPanelHead = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '13px 18px',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
  background: '#fafbfe',
});

const IssuesPanelTitle = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '0.9rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.87)',
});

const CategoryChips = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginLeft: 'auto',
  flexWrap: 'wrap',
});

const issuesGrid = {
  display: 'grid',
  gridTemplateColumns: '96px 158px minmax(0,1fr) 100px 150px',
  columnGap: '14px',
  alignItems: 'center',
};

const IssuesHeaderRow = styled('div')({
  ...issuesGrid,
  padding: '10px 18px',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
  background: '#fff',
});

const HeaderCell = styled('span')({
  fontSize: '0.78rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.6)',
});

const IssuesScrollWrap = styled('div')({
  position: 'relative',
});

const IssuesScroll = styled('div')({
  maxHeight: '300px',
  overflow: 'auto',
});

const IssueRowButton = styled('button')({
  ...issuesGrid,
  width: '100%',
  padding: '11px 18px',
  border: 'none',
  borderBottom: '1px solid rgba(0,0,0,0.07)',
  background: 'none',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background 0.15s ease',
  '&:hover': {
    background: '#f3f5fb',
  },
});

const CodeCell = styled('span')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,
});

const RosetteSlot = styled('span')({
  width: '16px',
  display: 'flex',
  justifyContent: 'center',
  flex: 'none',
});

const IssueTextCell = styled('span')({
  fontSize: '0.82rem',
  lineHeight: 1.42,
  color: 'rgba(0,0,0,0.72)',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

const DateCell = styled('span')({
  fontSize: '0.78rem',
  color: 'rgba(0,0,0,0.6)',
  whiteSpace: 'nowrap',
});

const ApprovedDate = styled(DateCell)({
  display: 'block',
});

const ResolvedCell = styled('span')({
  minWidth: 0,
});

const ResolvedBy = styled('span')({
  display: 'block',
  fontSize: '0.7rem',
  color: 'rgba(0,0,0,0.45)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const NoIssuesLine = styled('div')({
  padding: '34px 18px',
  textAlign: 'center',
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.5)',
});

const MoreFade = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: '46px',
  pointerEvents: 'none',
  background: 'linear-gradient(to bottom,rgba(255,255,255,0) 0%,rgba(255,255,255,0.92) 62%,#fff 100%)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  paddingBottom: '5px',
});

const MoreLabel = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '0.72rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.5)',
});

const PanelsGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit,minmax(380px,1fr))',
  gap: '18px',
  alignItems: 'stretch',
});

const Panel = styled('div')({
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: '6px',
  padding: '18px 20px',
});

const PanelTitle = styled('div')({
  fontSize: '0.9rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.87)',
  marginBottom: '14px',
});

const ScoreGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '14px',
});

const ScoreBox = styled('div', {
  shouldForwardProp: prop => prop !== 'subdued',
})<{ subdued?: boolean }>(({ subdued }) => ({
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: '6px',
  padding: '14px 16px',
  background: subdued ? '#fafbfe' : '#fff',
}));

const ScoreCaption = styled('div')({
  fontSize: '0.8rem',
  color: 'rgba(0,0,0,0.6)',
});

const ScoreValue = styled('div', {
  shouldForwardProp: prop => prop !== 'muted',
})<{ muted?: boolean }>(({ muted }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: muted ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.87)',
  marginTop: '6px',
}));

const ScoreInputRow = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  gap: '6px',
  marginTop: '6px',
});

const ScoreInput = styled('input')({
  width: '74px',
  boxSizing: 'border-box',
  border: '1px solid rgba(0,0,0,0.42)',
  borderRadius: '4px',
  padding: '6px 10px',
  fontFamily: 'inherit',
  fontSize: '1.2rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.87)',
  outline: 'none',
  '&:focus': {
    borderColor: ACCENT_BLUE,
    boxShadow: `inset 0 0 0 1px ${ACCENT_BLUE}`,
  },
});

const PercentSign = styled('span')({
  fontSize: '1.2rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.87)',
});

const UnchangedRow = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  marginTop: '6px',
  flexWrap: 'wrap',
});

const UnchangedNote = styled('span')({
  fontSize: '0.72rem',
  color: 'rgba(0,0,0,0.45)',
});

const MatchesLine = styled('div')({
  fontSize: '0.72rem',
  color: 'rgba(0,0,0,0.42)',
  marginTop: '4px',
});

const OverrideRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginTop: '16px',
  cursor: 'pointer',
});

const OverrideBox = styled('span', {
  shouldForwardProp: prop => prop !== 'on',
})<{ on: boolean }>(({ on }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '3px',
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: on ? `2px solid ${ACCENT_BLUE}` : '2px solid rgba(0,0,0,0.55)',
  background: on ? ACCENT_BLUE : '#fff',
  boxSizing: 'border-box',
}));

const OverrideLabel = styled('span')({
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.8)',
});

const ReasonTextarea = styled('textarea')({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '70px',
  marginTop: '12px',
  border: '1px solid rgba(0,0,0,0.42)',
  borderRadius: '4px',
  padding: '10px 12px',
  fontFamily: 'inherit',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.87)',
  resize: 'vertical',
  outline: 'none',
  '&:focus': {
    borderColor: ACCENT_BLUE,
    boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
  },
});

const NotesField = styled('span')({
  position: 'relative',
  display: 'block',
});

const NotesLabel = styled('span')({
  position: 'absolute',
  top: '-7px',
  left: '11px',
  background: '#fff',
  padding: '0 5px',
  fontSize: '0.72rem',
  color: ACCENT_BLUE,
  pointerEvents: 'none',
  lineHeight: 1,
});

const NotesTextarea = styled('textarea')({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '200px',
  border: `1px solid ${ACCENT_BLUE}`,
  borderRadius: '4px',
  padding: '12px 14px',
  fontFamily: 'inherit',
  fontSize: '0.95rem',
  color: 'rgba(0,0,0,0.87)',
  resize: 'vertical',
  outline: 'none',
});

const NotesHint = styled('div')({
  fontSize: '0.78rem',
  color: 'rgba(0,0,0,0.55)',
  marginTop: '8px',
});

const Footer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '18px 32px 22px',
  borderTop: '1px solid rgba(0,0,0,0.12)',
});

const FooterNote = styled('span')({
  fontSize: '0.8125rem',
  color: 'rgba(0,0,0,0.6)',
});

const FooterActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const ErrorNote = styled('span')({
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: ERROR_RED,
});

export interface ApproveAssessmentModalProps {
  open: boolean;
  companyName?: string;
  unitLabel?: string;
  assessorName?: string;
  rows: QcSummaryRow[];
  proposedScore: number;
  onGoToQuestion: (row: QcSummaryRow) => void;
  onApprove: (finalScore: string, overrideReason: string, emailNotes: string) => void | Promise<void>;
  onClose: () => void;
}

export default function ApproveAssessmentModal(props: ApproveAssessmentModalProps): JSX.Element {
  const {
    open, companyName, unitLabel, assessorName, rows, proposedScore,
    onGoToQuestion, onApprove, onClose,
  } = props;
  const [override, setOverride] = useState(false);
  const [finalScore, setFinalScore] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [emailNotes, setEmailNotes] = useState('');
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState('');

  const approvedCount = rows.filter(row => row.issue.status === 'approved').length;
  const categories = countQcCategories(rows);
  const canSubmit = !override || (overrideReason.trim().length > 0 && finalScore !== '');
  const effectiveScore = override && finalScore !== '' ? finalScore : String(proposedScore);
  const moreCount = rows.length - VISIBLE_ROWS;

  const toggleOverride = () => {
    const next = !override;
    setOverride(next);
    setFinalScore(next ? String(proposedScore) : '');
    setOverrideReason('');
  };

  // Single-flight: confirming emails the assessor, so a double-click must never fire twice.
  const handleApprove = async () => {
    if (!canSubmit || approving) return;
    setApproving(true);
    setApproveError('');
    try {
      await onApprove(effectiveScore, override ? overrideReason.trim() : '', emailNotes.trim());
    } catch {
      setApproveError('Finalization failed — the score and email were not sent. Try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleClose = () => {
    if (approving) return;
    setApproveError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-label='Approve assessment'
      PaperProps={{
        sx: {
          width: 1120,
          maxWidth: '100%',
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px',
        },
      }}
      maxWidth={false}
    >
      <Header>
        <div style={{ minWidth: 0 }}>
          <Title>Finalize Assessment QC</Title>
          <Lede>
            Every QC issue on this assessment has been approved. Review the summary and confirm the
            final score — finalizing emails the assessor and lets the service provider continue with
            Remediation.
          </Lede>
        </div>
        <CloseButton type='button' aria-label='Close approve assessment' onClick={handleClose}>
          <CloseIcon />
        </CloseButton>
      </Header>
      <MetaStrip>
        <MetaItem>
          <MetaLabel>Vendor</MetaLabel>
          <MetaValue strong>{companyName || ''}</MetaValue>
        </MetaItem>
        <MetaDivider />
        <MetaItem>
          <MetaLabel>Site or App</MetaLabel>
          <MetaValue>{unitLabel || ''}</MetaValue>
        </MetaItem>
        <MetaDivider />
        <MetaItem>
          <MetaLabel>Assessor</MetaLabel>
          <MetaValue>{assessorName || ''}</MetaValue>
        </MetaItem>
      </MetaStrip>
      <Body>
        <IssuesPanel>
          <IssuesPanelHead>
            <IssuesPanelTitle>
              <CheckCircleIcon sx={{ fontSize: 18, color: SUCCESS_GREEN }} />
              Reviewed QC Issues ({approvedCount})
            </IssuesPanelTitle>
            <CategoryChips>
              {categories.map(({ category, count }) => (
                <CategoryChip key={category} rgb={QC_CATEGORY_RGB[category]}>
                  {QC_ISSUE_CATEGORY_LABELS[category]} · {count}
                </CategoryChip>
              ))}
            </CategoryChips>
          </IssuesPanelHead>
          <IssuesHeaderRow>
            <HeaderCell>Question</HeaderCell>
            <HeaderCell>Category</HeaderCell>
            <HeaderCell>QC Issue</HeaderCell>
            <HeaderCell>Created</HeaderCell>
            <HeaderCell>Reviewed</HeaderCell>
          </IssuesHeaderRow>
          <IssuesScrollWrap>
            <IssuesScroll>
              {rows.map(row => (
                <IssueRowButton key={row.issue.id} type='button' onClick={() => onGoToQuestion(row)}>
                  <CodeCell>
                    <RosetteSlot>
                      {row.entry.question.isBestPractice && (
                        <WorkspacePremiumIcon titleAccess='Best Practice question' sx={{ fontSize: 15, color: '#D467E4' }} />
                      )}
                    </RosetteSlot>
                    <QuestionCode>{row.entry.question.shortCode}</QuestionCode>
                  </CodeCell>
                  <CategoryChip rgb={QC_CATEGORY_RGB[row.issue.category]}>
                    {QC_ISSUE_CATEGORY_LABELS[row.issue.category]}
                  </CategoryChip>
                  <IssueTextCell title={row.issue.text}>{row.issue.text}</IssueTextCell>
                  <DateCell>{formatQcDate(row.issue.createdAt)}</DateCell>
                  <ResolvedCell>
                    <ApprovedDate>{formatQcDate(row.issue.statusAt || row.issue.createdAt)}</ApprovedDate>
                    {row.issue.statusBy && <ResolvedBy>by {row.issue.statusBy}</ResolvedBy>}
                  </ResolvedCell>
                </IssueRowButton>
              ))}
              {!rows.length && <NoIssuesLine>No QC issues were raised on this assessment.</NoIssuesLine>}
            </IssuesScroll>
            {moreCount > 0 && (
              <MoreFade>
                <MoreLabel>
                  <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                  {moreCount} more
                </MoreLabel>
              </MoreFade>
            )}
          </IssuesScrollWrap>
        </IssuesPanel>
        <PanelsGrid>
          <Panel>
            <PanelTitle>QC Score Review</PanelTitle>
            <ScoreGrid>
              <ScoreBox>
                <ScoreCaption>Proposed Score</ScoreCaption>
                <ScoreValue>{proposedScore}%</ScoreValue>
              </ScoreBox>
              <ScoreBox subdued={!override}>
                <ScoreCaption>Final Score</ScoreCaption>
                {override && (
                  <ScoreInputRow>
                    <ScoreInput
                      aria-label='Final score'
                      value={finalScore}
                      onChange={event => setFinalScore(event.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                    />
                    <PercentSign>%</PercentSign>
                  </ScoreInputRow>
                )}
                {!override && (
                  <>
                    <UnchangedRow>
                      <ScoreValue muted>{proposedScore}%</ScoreValue>
                      <UnchangedNote>unchanged</UnchangedNote>
                    </UnchangedRow>
                    <MatchesLine>Matches the proposed score</MatchesLine>
                  </>
                )}
              </ScoreBox>
            </ScoreGrid>
            <OverrideRow role='checkbox' aria-checked={override} tabIndex={0} onClick={toggleOverride}>
              <OverrideBox on={override}>
                {override && <CheckIcon sx={{ fontSize: 11, color: '#fff' }} />}
              </OverrideBox>
              <OverrideLabel>Override proposed score and provide reason</OverrideLabel>
            </OverrideRow>
            {override && (
              <ReasonTextarea
                aria-label='Override reason'
                placeholder='Reason for overriding the proposed score (required)'
                value={overrideReason}
                onChange={event => setOverrideReason(event.target.value)}
              />
            )}
          </Panel>
          <Panel>
            <PanelTitle>Assessor Email Notes</PanelTitle>
            <NotesField>
              <NotesLabel>Notes</NotesLabel>
              <NotesTextarea
                aria-label='Assessor email notes'
                placeholder='Notes to be sent to the assessor via email…'
                value={emailNotes}
                onChange={event => setEmailNotes(event.target.value)}
              />
            </NotesField>
            <NotesHint>These notes will be included in the email sent to the assessor.</NotesHint>
          </Panel>
        </PanelsGrid>
      </Body>
      <Footer>
        <FooterNote>
          {rows.length > 0
            ? `${approvedCount} of ${rows.length} QC issues reviewed · clicking an issue opens that question`
            : 'No QC issues were raised on this assessment'}
        </FooterNote>
        <FooterActions>
          {approveError && <ErrorNote role='alert'>{approveError}</ErrorNote>}
          <OutlinedButton type='button' disabled={approving} onClick={handleClose}>Cancel</OutlinedButton>
          <PrimaryButton type='button' disabled={!canSubmit || approving} onClick={handleApprove}>
            {approving ? 'Finalizing…' : 'Finalize Assessment QC'}
          </PrimaryButton>
        </FooterActions>
      </Footer>
    </Dialog>
  );
}
